import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  Typography,
  Box,
  Container,
  Divider,
  IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import LeftSideNavbar from "../components/leftSideNavbar.component";
import { FacilityStyles, PageStyles } from "../styles";
import TopBreadcrumbs from "../components/topBreadcrumbs.component";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
// Import the FAQ data directly
import faqData from "../data/faqData.json";
import { selectOrganisationId } from "../selectors/login.selector";
import { getClientStatus } from "../utils/lib";

const FaqPage = () => {
  // Update state initialization - we already have the data, just need loading status
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState({});
  
  // Get organization ID and client status
  const orgId = useSelector(selectOrganisationId);
  const clientStatus = getClientStatus(orgId);

  useEffect(() => {
    try {
      // Initialize the open states from the directly imported data
      const initialOpenStates = {};
      faqData.sections.forEach(section => {
        initialOpenStates[`section-${section.id}`] = false;
        section.questions.forEach(question => {
          initialOpenStates[`question-${section.id}-${question.id}`] = false;
        });
      });
      setOpenSections(initialOpenStates);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to process FAQ data:", error);
      setError(error.message);
      setIsLoading(false);
    }
  }, []);

  const toggleItem = (key) => {
    setOpenSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Helper function to replace 4imprint with dynamic client name
  const replaceClientName = (text) => {
    if (!clientStatus.clientName) return text;
    return text.replace(/4imprint/gi, clientStatus.clientName);
  };

  // Simple text rendering without markdown
  const renderContent = (content) => {
    if (Array.isArray(content)) {
      return content.map((item, index) => (
        <Typography 
          key={index} 
          variant="body2" 
          sx={{ 
            mb: 1.5,
            fontSize: "0.875rem",
            color: "#555",
            lineHeight: 1.6,
            whiteSpace: "pre-line" // Preserves line breaks
          }}
        >
          {replaceClientName(item)}
        </Typography>
      ));
    }
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          mb: 1.5,
          fontSize: "0.875rem",
          color: "#555",
          lineHeight: 1.6,
          whiteSpace: "pre-line"
        }}
      >
        {replaceClientName(content)}
      </Typography>
    );
  };

  return (
    <Box sx={PageStyles.containerBox}>
      <LeftSideNavbar />
      <Box sx={PageStyles.containerBoxExceptNavbar}>
        <Box sx={PageStyles.firstSectionWithBreadcrumbsAndName}>
          <TopBreadcrumbs />
          <Grid container justifyContent="space-between">
            <Grid item xs={12}>
              <Typography gutterBottom sx={PageStyles.name}>
                {faqData?.title || "How to use the platform"}
              </Typography>
              {faqData && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 2, 
                    color: "#555",
                    lineHeight: 1.5,
                    fontSize: "0.95rem"
                  }}
                >
                  {replaceClientName(faqData.introduction)}
                </Typography>
              )}
            </Grid>
            <Grid item></Grid>
          </Grid>
        </Box>
        <Divider />
        <Container sx={{ 
          py: 4, 
          maxWidth: "100% !important", 
          height: "calc(100vh - 180px)",
          display: "flex",
          flexDirection: "column"
        }}>
          {isLoading ? (
            <Typography variant="body1">Loading FAQ...</Typography>
          ) : error ? (
            <Typography variant="body1" color="error">
              Error loading FAQ: {error}
            </Typography>
          ) : (
            <Box sx={{ 
              mb: 2, 
              flexGrow: 1,
              display: "flex",
              flexDirection: "column"
            }}>
              <Card sx={{ 
                ...FacilityStyles.containerCard, 
                pb: 4, 
                pt: 2,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
              }}>
                <Box sx={{ flexGrow: 1, overflowY: "auto", px: 3 }}>
                  {faqData.sections.map((section, index) => (
                    <Box key={section.id} sx={{ mb: index === 0 ? 16 : 4 }}>
                      <Box sx={{ mb: 3, mt: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "1.6rem",
                            fontWeight: 700,
                            color: "#333",
                            mb: 1,
                          }}
                        >
                          {replaceClientName(section.title)}
                        </Typography>
                        
                        {section.introduction && (
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              mb: 2, 
                              color: "#555", 
                              fontSize: "0.95rem",
                              lineHeight: 1.5
                            }}
                          >
                            {replaceClientName(section.introduction)}
                          </Typography>
                        )}
                      </Box>

                      {section.questions.map((question) => {
                        const questionKey = `question-${section.id}-${question.id}`;
                        const isOpen = openSections[questionKey];
                        
                        return (
                          <React.Fragment key={question.id}>
                            <Box sx={{ p: 2, border: "1px solid #eee", borderRadius: "8px", mb: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                    color: "#333",
                                    pr: 2,
                                    lineHeight: 1.5,
                                    flex: 1,
                                  }}
                                >
                                  {replaceClientName(question.title)}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleItem(questionKey)}
                                  sx={{
                                    bgcolor: "#eeeeee",
                                    color: "#666666",
                                    "&:hover": {
                                      bgcolor: "#dddddd",
                                    },
                                    width: 32,
                                    height: 32,
                                    minWidth: 32,
                                    minHeight: 32,
                                  }}
                                >
                                  {isOpen ? (
                                    <ExpandLessIcon sx={{ fontSize: 20 }} />
                                  ) : (
                                    <ExpandMoreIcon sx={{ fontSize: 20 }} />
                                  )}
                                </IconButton>
                              </Box>
                              {isOpen && (
                                <Box 
                                  sx={{ 
                                    mt: 2,
                                    pt: 2,
                                    borderTop: "1px solid #eee"
                                  }}
                                >
                                  {renderContent(question.content)}
                                </Box>
                              )}
                            </Box>
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Card>
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default FaqPage;